import { useEffect, useState } from "react";
import { T, TF } from "react-translator-component"
import { Avatar, Box, Card, CardContent, ListItem, Stack, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import GCBackButton from "../../../components/GreenCity/GCBackButton";
import { useSelector } from "react-redux";
import appServices from "../../../services/app-services/appServices";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PushPinIcon from "@mui/icons-material/PushPin";
import MultiMenuModal from "../../../components/Models/MultiMenuModal";
import { ChatList, Createchat } from "../../../util/types";
import LoadingModal from "../../../components/LoadingModal";
import { convertUserImage, getChatUserName } from "../../../util/utils";
import { ErrorMessageModal } from "../../../components/ErrorMessageModal";

const GroupChatList = () => {
  const profileImage = require("../../../assets/images/profile_img.png")

  const navigate = useNavigate();
  const location = useLocation();
  const { groupInfo } = location?.state || {};
  const { user } = useSelector((state: any) => state.userAuth);

  const [allChats, setAllChats] = useState<any>([]);
  // eslint-disable-next-line
  const [loading, setLoading] = useState<boolean>(false);
  const [groupMembers, setGroupMembers] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [groupChatInfo, setGroupChatInfo] = useState<any>(null);
  const [openOption, setOpenOptions] = useState<boolean>(false);
  const [openSingleOption, setOpenSingleOption] = useState<boolean>(false);
  const [selectedChat, setSelectedChat] = useState<any>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<any>(undefined);
  const [selectedName, setSelectedName] = useState<string>();
  const [groupIcon, setGroupIcon] = useState<any>(undefined);
  const [openErrorMessageModal, setOpenErrorMessageModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string[]>([]);

  const getGroupIcon = (groupId: number) => {
    if (!groupId) {
      setGroupIcon(undefined);
      return
    }

    appServices.getGroupIcon(groupId).then(blob => {
      setGroupIcon(URL.createObjectURL(blob));
    }).catch(() => {
      console.error("Failed to get group icon image")
      setGroupIcon(undefined);
    })
  }

  const checkIfGroupChatExists = () => {
    appServices.showGroupChat(groupInfo?.id).then((response: any) => {
      const groupChat = response?.chats[0];
      setGroupChatInfo(groupChat);
    });
  };

  const getGroupMembers = async () => {
    const res = await appServices.getMemberList(groupInfo?.id);
    setGroupMembers(res?.member_group_users);
  };

  const getChatList = () => {
    appServices
      .getChatList(groupInfo?.id)
      .then((response: any) => {
        setAllChats(response.chats.filter((chat: any) => chat.chat_type !== "group"));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };
  // eslint-disable-next-line
  useEffect(() => {
    getGroupIcon(groupInfo?.id)
    setLoading(true);
    checkIfGroupChatExists();
    getGroupMembers();
    getChatList();
    // eslint-disable-next-line
  }, [groupInfo]);

  const getMemberImage = (memberId: number) => {
    let memberInfo;
    if (groupInfo?.owner?.id === memberId) {
      memberInfo = groupInfo?.owner
    } else {
      memberInfo = groupInfo?.member_group_users?.find((groupMember: any) => groupMember.id === memberId);
    }

    return memberInfo?.image ? convertUserImage(memberInfo.image) : profileImage;
  };

  const handleOptionsModal = () => {
    setOpenOptions(!openOption);
  };

  const handleGroupSelection = async (path: string) => {
    let targetChat;

    if (path === "/group_chat") {
      if (!groupChatInfo) {
        const chatPayload: Createchat = {
          chat_type: "group",
          member_group_id: groupInfo?.id,
          users: groupMembers.map((member) => ({
            user_id: member.user.id,
            can_message: true,
          })),
        };

        try {
          setLoading(true);
          const response = await appServices.createChat(chatPayload);

          targetChat = response.data.chat
        } catch (error: any) {
          console.error(error)
          setErrorMessage(error?.response?.data?.message ?? T("api_unknown_error"));
          setOpenErrorMessageModal(true)
          return
        } finally {
          setOpenSingleOption(false)
          setOpenOptions(false)
          setLoading(false);
        }
      } else {
        targetChat = groupChatInfo
      }
    } else if (path === "/open_chat") {
      targetChat = selectedChat
    }

    navigate("/userchat", {
      state: {
        chatId: targetChat?.id
      },
    });
  };

  // Define static options
  const staticOptions = [{ title: T("group_chat_list_static_option"), path: "/group_chat", disabled: false }];

  const getChatOption = () => {
    let chatOption = [
      { title: T("see_posts"), path: "", disabled: true },
      { title: T("chats"), path: "/open_chat", disabled: false },
      { title: T("switch_role"), path: "", disabled: true },
      { title: T("group_chat_list_add_to_group_chat"), path: "", disabled: true },
      { title: T("kick"), path: "", disabled: true },
    ];
    return chatOption;
  };


  return (
    <Box>
      <Box style={style.headerContainer}>
        <GCBackButton onClick={() => navigate(-1)} />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box style={style.eventPostingBox}>
            <Box component="div" style={style.eventPostingIcon}>
            <Avatar src={groupIcon} sx={{ width: 30, height: 30 }} />
            </Box>
            <Typography style={{ fontWeight: "700", marginTop: "5px" }}>
              <span>{TF("{0}{1}", groupInfo?.name, T("chat_list"))}</span>
            </Typography>
          </Box>
        </Box>

        <Box>
          <ListItem disableGutters>
            <Card sx={{ width: "100%", boxShadow: "none" }}>
              <CardContent sx={{ paddingBottom: "0px !important" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar src={groupIcon} sx={{ width: 40, height: 40 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {groupInfo?.name}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <PushPinIcon sx={{ color: "grey", transform: "rotate(45deg)", fontSize: "28px" }} />
              <Box sx={{ margin: "0 5px" }} style={style.optionButton} onClick={handleOptionsModal}>
                <MoreHorizIcon sx={{ color: "#4792E0", cursor: "pointer" }} />
              </Box>
            </Box>

            <MultiMenuModal
              userName={groupInfo?.name}
              avatars={[groupIcon]}
              open={openOption}
              onClose={handleOptionsModal}
              options={staticOptions}
              onSelect={handleGroupSelection}
            />

          </ListItem>
        </Box>
        {allChats.map((chat: ChatList) => {
          return (
            <Box key={chat.id}>
              <ListItem sx={{ borderTop: "1px solid #D3D3D3" }} disableGutters>
                <Card sx={{ width: "100%", boxShadow: "none" }}>
                  <CardContent sx={{ paddingBottom: "0px !important", paddingTop: "0" }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        src={getMemberImage(chat?.users[1]?.id) ?? undefined}
                        sx={{ width: 40, height: 40 }}
                      />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {getChatUserName(chat?.users, user.id)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{ margin: "0 5px" }}
                    style={style.optionButton}
                    onClick={() => {
                      setOpenSingleOption(true);
                      setSelectedChat(chat);
                      const userAvatars = chat.users.map((user) => getMemberImage(user.id));
                      setSelectedAvatar(userAvatars);
                      setSelectedName(
                        chat.users
                          .slice(0, 4)
                          .map((user) => user.nickname)
                          .join(", ") + (chat.users.length > 3 ? " +" : "")
                      );
                    }}
                  >
                    <MoreHorizIcon sx={{ color: "#4792E0", cursor: "pointer" }} />
                  </Box>
                </Box>
              </ListItem>
            </Box>
          );
        })}

        <MultiMenuModal
          open={openSingleOption}
          onClose={() => setOpenSingleOption(false)}
          options={getChatOption()}
          onSelect={handleGroupSelection}
          avatars={selectedAvatar} // Pass avatar separately
          userName={selectedName || ""} // Pass name separately
        />

        <ErrorMessageModal
          open={openErrorMessageModal}
          messages={errorMessage}
          onClose={() => setOpenErrorMessageModal(false)}
        />

      </Box>
      <LoadingModal visible={loading} handleClose={() => {}} />
    </Box>
  );
};

const style = {
  optionButton: {
    border: "1px solid #4792E0",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "3px",
  },
  eventPostingIcon: {
    borderRadius: "50%",
    padding: "3px 6px 3px 6px",
    color: "white",
  },
  eventPostingBox: {
    display: "flex",
    gap: "15px",
    alignItems: "center",
    paddingTop: "10px",
    paddingLeft: "10px",
  },
  headerContainer: {
    paddingTop: 100,
    paddingRight: 16,
    paddingBottom: "12px",
  },
};

export default GroupChatList;

